import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Modal,
  ModalFooter,
  Select,
  getStatusBadgeVariant,
} from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  AUDIENCE_HINTS,
  AUDIENCE_LABELS,
  DECISION_TARGETS,
  awaitingInsurerDecision,
  claimsDeskService,
  isStaleClaimError,
  type ClaimDocumentRequest,
  type DocumentRequestItem,
  type InsurerDeskClaim,
  type RemarkAudience,
} from '@/services/claimsDesk';
import { useClaimStatusTransitions } from '@/hooks/useClaims';
import { formatCurrency, formatDate, formatDateTime, titleCase } from '@/utils';
import {
  CLAIM_DOCUMENT_TYPES,
  claimDocumentsApi,
  formatFileSize,
  readList,
  type ClaimDocumentRow,
} from './claimDocuments';

/**
 * #401: the insurer Claims Desk on the claim detail page (Phase 1).
 *
 * Action bar (by status and permission), the documents the insurer may read
 * and download, the document requests and the curated history. Every button
 * mirrors a server rule -- the graph (`/claims/transitions/` serves the
 * insurer's own), `claims.edit` / `claims.approve` (`can_edit` / `can_decide`
 * on the payload) and the amount limit (`my_decision_limit`) -- and the
 * server re-checks each one, so a stale screen gets a 409 and a refetch.
 */

type DeskModal = 'status' | 'documents' | 'remark' | 'approve' | 'reject' | 'settle' | null;

const PAYMENT_MODES = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'demand_draft', label: 'Demand Draft' },
  { value: 'direct_to_garage', label: 'Direct to Garage' },
  { value: 'direct_to_hospital', label: 'Direct to Hospital' },
];

const AUDIENCES: RemarkAudience[] = ['insurer_only', 'broker', 'customer'];

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white';

function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** "Above your limit of INR X" when the amount is over it, else null. */
// eslint-disable-next-line react-refresh/only-export-components
export function limitBlockReason(
  limit: string | null | undefined,
  amount: number | null
): string | null {
  if (limit === null || limit === undefined) return null; // insurer admin: no limit
  const cap = Number(limit);
  if (!cap)
    return 'No claims approval limit is set for your company: only your insurer admin can decide.';
  if (amount === null) return 'The amount is not known: only your insurer admin can decide.';
  if (amount > cap) return `Above your limit of ${formatCurrency(cap)}: needs the insurer admin.`;
  return null;
}

function AudiencePicker({
  name,
  value,
  onChange,
}: {
  name: string;
  value: RemarkAudience;
  onChange: (next: RemarkAudience) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
        Who reads this?
      </legend>
      <div className="flex flex-wrap gap-4">
        {AUDIENCES.map((audience) => (
          <label
            key={audience}
            className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
          >
            <input
              type="radio"
              name={name}
              value={audience}
              checked={value === audience}
              onChange={() => onChange(audience)}
            />
            {AUDIENCE_LABELS[audience]}
          </label>
        ))}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{AUDIENCE_HINTS[value]}</p>
    </fieldset>
  );
}

export function InsurerClaimDesk({ claim }: { claim: InsurerDeskClaim }) {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<DeskModal>(null);
  const claimId = claim.id;

  const { data: transitions } = useClaimStatusTransitions();
  const statusOptions = (transitions?.[claim.status] ?? []).filter(
    (o) => !DECISION_TARGETS.includes(o.value)
  );

  const history = useQuery({
    queryKey: ['claim-history', claimId],
    queryFn: () => claimsDeskService.history(claimId),
  });
  const requests = useQuery({
    queryKey: ['claim-document-requests', claimId],
    queryFn: () => claimsDeskService.documentRequests(claimId),
  });
  const documents = useQuery({
    queryKey: ['claim-documents-insurer', claimId],
    queryFn: async () => readList<ClaimDocumentRow>(await claimDocumentsApi.list(claimId)),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['claim', claimId] });
    queryClient.invalidateQueries({ queryKey: ['claims'] });
    queryClient.invalidateQueries({ queryKey: ['claim-history', claimId] });
    queryClient.invalidateQueries({ queryKey: ['claim-document-requests', claimId] });
  };

  /** A 409 means the claim moved under us: say so and redraw. */
  const onDeskError = (error: unknown) => {
    if (isStaleClaimError(error)) {
      toast.error(getErrorMessage(error));
      refresh();
      setModal(null);
      return;
    }
    toast.error(getErrorMessage(error));
  };

  const canEdit = !!claim.can_edit;
  const canDecide = !!claim.can_decide;
  const terminal = ['settled', 'closed', 'rejected', 'withdrawn'].includes(claim.status);
  const awaiting = awaitingInsurerDecision(claim);
  const claimed = toNumber(claim.claimed_amount);
  const approved = toNumber(claim.approved_amount);
  const rejectBlock = canDecide ? limitBlockReason(claim.my_decision_limit, claimed) : null;

  return (
    <>
      <Card>
        <CardHeader title="Claims Desk" subtitle={deskSubtitle(claim)} />
        <div className="flex flex-wrap gap-2" role="toolbar" aria-label="Claims desk actions">
          {canEdit && statusOptions.length > 0 && (
            <Button variant="outline" onClick={() => setModal('status')}>
              Change status
            </Button>
          )}
          {canEdit && !terminal && (
            <Button variant="outline" onClick={() => setModal('documents')}>
              Request documents
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={() => setModal('remark')}>
              Add remark
            </Button>
          )}
          {canDecide && awaiting && (
            <>
              <Button onClick={() => setModal('approve')}>Approve</Button>
              <Button
                variant="danger"
                onClick={() => setModal('reject')}
                disabled={!!rejectBlock}
                title={rejectBlock ?? undefined}
              >
                Reject
              </Button>
            </>
          )}
          {canDecide && claim.status === 'settlement_pending' && (
            <Button onClick={() => setModal('settle')}>Settle</Button>
          )}
          {!canEdit && !canDecide && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your role can view this claim. Ask your insurer admin for claims access to work it.
            </p>
          )}
        </div>
        {canDecide && claim.my_decision_limit !== null && claim.my_decision_limit !== undefined && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {Number(claim.my_decision_limit)
              ? `Your approval limit: ${formatCurrency(Number(claim.my_decision_limit))}.`
              : 'No claims approval limit is set for your company yet, so only your insurer admin can decide.'}
          </p>
        )}
        <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Fact label="Insurer claim number" value={claim.insurer_claim_number} />
          <Fact label="Claimant" value={claim.claimant_name} />
          <Fact label="Cashless" value={claim.is_cashless ? 'Yes' : 'No'} />
          <Fact label="Diagnosis" value={claim.diagnosis} />
          <Fact
            label="FIR"
            value={
              claim.fir_number ? `${claim.fir_number} (${formatDate(claim.fir_date ?? '')})` : ''
            }
          />
          <Fact label="Police report filed" value={claim.police_report_filed ? 'Yes' : 'No'} />
          {claim.insurer_decision && (
            <Fact
              label="Insurer decision"
              value={`${titleCase(claim.insurer_decision)}${
                claim.insurer_decided_by_name ? ` by ${claim.insurer_decided_by_name}` : ''
              } on ${formatDateTime(claim.insurer_decided_at ?? '')}`}
            />
          )}
          {claim.rejection_reason && (
            <Fact label="Rejection reason" value={claim.rejection_reason} />
          )}
        </dl>
      </Card>

      <DocumentRequestsCard
        requests={requests.data ?? []}
        isLoading={requests.isLoading}
        canCancel={canEdit}
        claimId={claimId}
        onChanged={refresh}
      />

      <Card>
        <CardHeader title="Documents" subtitle="Uploaded by the broker or the customer" />
        {documents.isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading documents...</p>
        ) : documents.isError ? (
          <p role="alert" className="text-sm text-red-600">
            Could not load the documents.
          </p>
        ) : (documents.data ?? []).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No documents uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {(documents.data ?? []).map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                    {doc.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(doc as ClaimDocumentRow & { document_type_display?: string })
                      .document_type_display ?? doc.document_type}{' '}
                    · {formatFileSize(doc.file_size)} · {doc.uploaded_by_name || '—'} ·{' '}
                    {formatDate(doc.created_at)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    claimDocumentsApi
                      .download(claimId, doc)
                      .catch((e) => toast.error(getErrorMessage(e)))
                  }
                >
                  Download
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="Claim History" subtitle="Lifecycle events and shared remarks" />
        {history.isLoading ? (
          <p className="text-sm text-gray-500">Loading history...</p>
        ) : (history.data ?? []).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No history yet.</p>
        ) : (
          <ol className="space-y-4">
            {(history.data ?? []).map((entry) => (
              <li key={entry.id} className="border-l-2 border-gray-200 pl-3 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {entry.action_display}
                  </span>
                  {entry.to_status && entry.to_status !== entry.from_status && (
                    <Badge variant={getStatusBadgeVariant(entry.to_status)} size="sm">
                      {titleCase(entry.to_status)}
                    </Badge>
                  )}
                  {entry.audience && (
                    <Badge
                      variant={entry.audience === 'insurer_only' ? 'default' : 'info'}
                      size="sm"
                    >
                      {AUDIENCE_LABELS[entry.audience]}
                    </Badge>
                  )}
                </div>
                {entry.description && (
                  <p className="mt-1 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">
                    {entry.description}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  {formatDateTime(entry.created_at)}
                  {entry.actor_name ? ` · ${entry.actor_name}` : ''}
                </p>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {modal === 'status' && (
        <StatusModal
          claim={claim}
          options={statusOptions}
          onClose={() => setModal(null)}
          onDone={refresh}
          onError={onDeskError}
        />
      )}
      {modal === 'documents' && (
        <RequestDocumentsModal
          claim={claim}
          onClose={() => setModal(null)}
          onDone={refresh}
          onError={onDeskError}
        />
      )}
      {modal === 'remark' && (
        <RemarkModal
          claimId={claimId}
          onClose={() => setModal(null)}
          onDone={refresh}
          onError={onDeskError}
        />
      )}
      {modal === 'approve' && (
        <ApproveModal
          claim={claim}
          onClose={() => setModal(null)}
          onDone={refresh}
          onError={onDeskError}
        />
      )}
      {modal === 'reject' && (
        <RejectModal
          claim={claim}
          onClose={() => setModal(null)}
          onDone={refresh}
          onError={onDeskError}
        />
      )}
      {modal === 'settle' && (
        <SettleModal
          claim={claim}
          ceiling={approved ?? claimed}
          onClose={() => setModal(null)}
          onDone={refresh}
          onError={onDeskError}
        />
      )}
    </>
  );
}

function deskSubtitle(claim: InsurerDeskClaim): string {
  if (!claim.forwarded_to_insurer_at) return 'Your work on this claim';
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(claim.forwarded_to_insurer_at).getTime()) / 86_400_000)
  );
  return `With your insurer for ${days} day${days === 1 ? '' : 's'}`;
}

function Fact({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900 dark:text-white">{value ? value : '—'}</dd>
    </div>
  );
}

interface ModalProps {
  onClose: () => void;
  onDone: () => void;
  onError: (error: unknown) => void;
}

function useDeskMutation<T>(
  fn: () => Promise<T>,
  { onClose, onDone, onError }: ModalProps,
  message: string
) {
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(message);
      onDone();
      onClose();
    },
    onError,
  });
}

function StatusModal({
  claim,
  options,
  ...props
}: ModalProps & { claim: InsurerDeskClaim; options: { value: string; label: string }[] }) {
  const [status, setStatus] = useState('');
  const [note, setNote] = useState('');
  const [audience, setAudience] = useState<RemarkAudience>('broker');
  const [reference, setReference] = useState('');
  const needsNote = status === 'closed';
  const mutation = useDeskMutation(
    () =>
      claimsDeskService.changeStatus(claim.id, {
        status,
        expected_status: claim.status,
        note: note.trim(),
        note_audience: audience,
        insurer_claim_number: reference.trim(),
      }),
    props,
    'Claim status updated'
  );
  return (
    <Modal isOpen onClose={props.onClose} title="Change claim status">
      <div className="space-y-4">
        <Select
          id="desk-next-status"
          label="Next status"
          options={[{ value: '', label: 'Select the next step' }, ...options]}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        />
        {status === 'acknowledged' && !claim.insurer_claim_number && (
          <div>
            <label
              htmlFor="desk-insurer-ref"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Your claim number (optional)
            </label>
            <input
              id="desk-insurer-ref"
              className={inputClass}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
        )}
        <div>
          <label
            htmlFor="desk-status-note"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            {needsNote ? 'Why is the claim being closed?' : 'Note (optional)'}
          </label>
          <textarea
            id="desk-status-note"
            rows={3}
            className={inputClass}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {note.trim() && (
          <AudiencePicker name="desk-status-audience" value={audience} onChange={setAudience} />
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          isLoading={mutation.isPending}
          disabled={!status || (needsNote && !note.trim())}
        >
          Save status
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function RequestDocumentsModal({ claim, ...props }: ModalProps & { claim: InsurerDeskClaim }) {
  const [rows, setRows] = useState<DocumentRequestItem[]>([
    { document_type: '', description: '', due_date: '' },
  ]);
  const canMove = ['acknowledged', 'under_review'].includes(claim.status);
  const [move, setMove] = useState(true);
  const update = (index: number, patch: Partial<DocumentRequestItem>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const valid = rows.length > 0 && rows.every((row) => row.document_type);
  const mutation = useDeskMutation(
    () =>
      claimsDeskService.requestDocuments(
        claim.id,
        rows.map((row) => ({
          document_type: row.document_type,
          description: (row.description ?? '').trim(),
          due_date: row.due_date || null,
        })),
        canMove && move
      ),
    props,
    'Documents requested'
  );
  return (
    <Modal isOpen onClose={props.onClose} title="Request documents" size="lg">
      <div className="space-y-4">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-3">
            <Select
              id={`desk-doc-type-${index}`}
              label="Document"
              options={[{ value: '', label: 'Choose a document' }, ...CLAIM_DOCUMENT_TYPES]}
              value={row.document_type}
              onChange={(e) => update(index, { document_type: e.target.value })}
            />
            <div>
              <label
                htmlFor={`desk-doc-desc-${index}`}
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                What exactly
              </label>
              <input
                id={`desk-doc-desc-${index}`}
                className={inputClass}
                maxLength={1000}
                value={row.description ?? ''}
                onChange={(e) => update(index, { description: e.target.value })}
              />
            </div>
            <div>
              <label
                htmlFor={`desk-doc-due-${index}`}
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Due date (optional)
              </label>
              <input
                id={`desk-doc-due-${index}`}
                type="date"
                className={inputClass}
                value={row.due_date ?? ''}
                onChange={(e) => update(index, { due_date: e.target.value })}
              />
            </div>
          </div>
        ))}
        {rows.length < 10 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setRows((current) => [
                ...current,
                { document_type: '', description: '', due_date: '' },
              ])
            }
          >
            Add another document
          </Button>
        )}
        {canMove && (
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={move} onChange={(e) => setMove(e.target.checked)} />
            Move claim to Documents Pending
          </label>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The broker and the customer are told what you asked for.
        </p>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={props.onClose}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!valid}>
          Send request
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function RemarkModal({ claimId, ...props }: ModalProps & { claimId: string }) {
  const [text, setText] = useState('');
  const [audience, setAudience] = useState<RemarkAudience>('broker');
  const mutation = useDeskMutation(
    () => claimsDeskService.addRemark(claimId, text.trim(), audience),
    props,
    'Remark added'
  );
  return (
    <Modal isOpen onClose={props.onClose} title="Add remark">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="desk-remark"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Remark
          </label>
          <textarea
            id="desk-remark"
            rows={4}
            maxLength={2000}
            className={inputClass}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <AudiencePicker name="desk-remark-audience" value={audience} onChange={setAudience} />
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          isLoading={mutation.isPending}
          disabled={!text.trim()}
        >
          Add remark
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ApproveModal({ claim, ...props }: ModalProps & { claim: InsurerDeskClaim }) {
  const claimed = toNumber(claim.claimed_amount);
  const [amount, setAmount] = useState(claim.claimed_amount ? String(claim.claimed_amount) : '');
  const [remarks, setRemarks] = useState('');
  const [audience, setAudience] = useState<RemarkAudience>('broker');
  const value = toNumber(amount);
  const partial = value !== null && claimed !== null && value < claimed;
  const error =
    value === null || value <= 0
      ? 'Enter the approved amount.'
      : claimed !== null && value > claimed
        ? `Cannot approve more than the ${formatCurrency(claimed)} claimed.`
        : limitBlockReason(claim.my_decision_limit, value);
  const mutation = useDeskMutation(
    () =>
      claimsDeskService.approve(claim.id, {
        approved_amount: amount,
        partial,
        remarks: remarks.trim(),
        remarks_audience: audience,
        expected_status: claim.status,
      }),
    props,
    partial ? 'Claim partially approved' : 'Claim approved'
  );
  return (
    <Modal isOpen onClose={props.onClose} title="Approve claim">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="desk-approved-amount"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Approved amount
          </label>
          <input
            id="desk-approved-amount"
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {error ? (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          ) : (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {partial ? 'Less than claimed: this is a partial approval.' : 'Full approval.'}
              {claim.my_decision_limit
                ? ` Your limit: ${formatCurrency(Number(claim.my_decision_limit))}.`
                : ''}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="desk-approve-remarks"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Remarks (optional)
          </label>
          <textarea
            id="desk-approve-remarks"
            rows={3}
            className={inputClass}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>
        {remarks.trim() && (
          <AudiencePicker name="desk-approve-audience" value={audience} onChange={setAudience} />
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={props.onClose}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!!error}>
          {partial ? 'Approve partially' : 'Approve'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function RejectModal({ claim, ...props }: ModalProps & { claim: InsurerDeskClaim }) {
  const [reason, setReason] = useState('');
  const length = reason.trim().length;
  const mutation = useDeskMutation(
    () => claimsDeskService.reject(claim.id, reason.trim(), claim.status),
    props,
    'Claim rejected'
  );
  return (
    <Modal isOpen onClose={props.onClose} title="Reject claim">
      <div className="space-y-2">
        <label
          htmlFor="desk-reject-reason"
          className="block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Reason
        </label>
        <textarea
          id="desk-reject-reason"
          rows={4}
          maxLength={1000}
          className={inputClass}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The reason is stored on the claim and the customer will read it. Write at least 10
          characters.
        </p>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => mutation.mutate()}
          isLoading={mutation.isPending}
          disabled={length < 10}
        >
          Reject claim
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SettleModal({
  claim,
  ceiling,
  ...props
}: ModalProps & { claim: InsurerDeskClaim; ceiling: number | null }) {
  const [gross, setGross] = useState(claim.approved_amount ? String(claim.approved_amount) : '');
  const [tds, setTds] = useState('0');
  const [mode, setMode] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState('');
  const grossValue = toNumber(gross);
  const tdsValue = toNumber(tds) ?? 0;
  const net = grossValue === null ? null : Math.round((grossValue - tdsValue) * 100) / 100;
  const error =
    ceiling === null
      ? 'This claim has no approved or claimed amount on record, so it cannot be settled.'
      : grossValue === null || grossValue <= 0
        ? 'Enter the gross amount paid.'
        : net !== null && net < 0
          ? 'TDS cannot be more than the gross amount.'
          : net !== null && net > ceiling
            ? `Cannot settle more than the ${formatCurrency(ceiling)} approved.`
            : !reference.trim()
              ? 'Enter the payment reference (UTR or cheque number).'
              : limitBlockReason(claim.my_decision_limit, net);
  const mutation = useDeskMutation(
    () =>
      claimsDeskService.settle(claim.id, {
        gross_amount: gross,
        tds_amount: tds || '0',
        payment_mode: mode,
        payment_reference: reference.trim(),
        payment_date: paidOn,
        remarks: remarks.trim(),
        expected_status: claim.status,
      }),
    props,
    'Settlement recorded'
  );
  const field = (id: string, label: string, input: React.ReactNode) => (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
      >
        {label}
      </label>
      {input}
    </div>
  );
  return (
    <Modal isOpen onClose={props.onClose} title="Settle claim" size="lg">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {field(
          'desk-gross',
          'Gross amount',
          <input
            id="desk-gross"
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={gross}
            onChange={(e) => setGross(e.target.value)}
          />
        )}
        {field(
          'desk-tds',
          'TDS',
          <input
            id="desk-tds"
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={tds}
            onChange={(e) => setTds(e.target.value)}
          />
        )}
        <div>
          <p className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            Net paid
          </p>
          <p className="text-sm text-gray-900 dark:text-white" data-testid="desk-net">
            {net === null ? '—' : formatCurrency(net)}
          </p>
        </div>
        <Select
          id="desk-payment-mode"
          label="Payment mode"
          options={PAYMENT_MODES}
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        />
        {field(
          'desk-reference',
          'Payment reference',
          <input
            id="desk-reference"
            className={inputClass}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        )}
        {field(
          'desk-paid-on',
          'Payment date',
          <input
            id="desk-paid-on"
            type="date"
            className={inputClass}
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
          />
        )}
        <div className="sm:col-span-2">
          {field(
            'desk-settle-remarks',
            'Remarks (optional)',
            <textarea
              id="desk-settle-remarks"
              rows={2}
              className={inputClass}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          )}
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      <ModalFooter>
        <Button variant="outline" onClick={props.onClose}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!!error}>
          Record settlement
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * #401: the document requests on a claim. Shared by the insurer desk (with a
 * cancel control for its own requests) and the broker / customer claim page
 * (read-only: an upload of the requested type fulfils the request).
 */
export function DocumentRequestsCard({
  requests,
  isLoading,
  canCancel = false,
  claimId,
  onChanged,
}: {
  requests: ClaimDocumentRequest[];
  isLoading?: boolean;
  canCancel?: boolean;
  claimId: string;
  onChanged?: () => void;
}) {
  const cancel = useMutation({
    mutationFn: (id: string) => claimsDeskService.cancelRequest(claimId, id, 'No longer needed'),
    onSuccess: () => {
      toast.success('Request cancelled');
      onChanged?.();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  if (!isLoading && requests.length === 0 && !canCancel) return null;
  return (
    <Card>
      <CardHeader
        title="Document Requests"
        subtitle="Uploading a document of the requested type marks it received"
      />
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading requests...</p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No documents have been requested.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {requests.map((req) => (
            <li key={req.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {req.document_type_display}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {req.description ? `${req.description} · ` : ''}Asked by the{' '}
                  {req.requested_by_side}
                  {req.due_date ? ` · due ${formatDate(req.due_date)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    req.status === 'open'
                      ? 'warning'
                      : req.status === 'fulfilled'
                        ? 'success'
                        : 'default'
                  }
                  size="sm"
                >
                  {req.status_display}
                </Badge>
                {canCancel && req.status === 'open' && req.requested_by_side === 'insurer' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => cancel.mutate(req.id)}
                    isLoading={cancel.isPending}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
